import { useLocation, useNavigate } from '@tanstack/react-router';
import { Menu, LogOut, User, Settings, Moon, Sun } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { GlobalSearchBar } from '@/pages/crm/components/GlobalSearchBar';
import { BusinessLineSwitcher } from '@/features/crm/business-lines';
import { WorkspaceBreadcrumb } from '@/features/crm/business-units';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { NotificationCenter } from '@/features/notifications';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { useUser, useIsDarkMode, useUIActions } from '@/stores';
import { useLogout } from '@/features/auth/hooks/useAuth';
import { usePermissions } from '@/features/auth/hooks/usePermissions';
import { getRoleDisplayName } from '@/features/auth/utils';
import { cn } from '@/lib/utils';

interface HeaderProps {
  onMenuClick: () => void;
  sidebarCollapsed: boolean;
}

export function Header({ onMenuClick, sidebarCollapsed }: HeaderProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const user = useUser();
  const isDark = useIsDarkMode();
  const { toggleDarkMode } = useUIActions();
  const { hasPermission } = usePermissions();
  const isCrmRoute = location.pathname === '/crm' || location.pathname.startsWith('/crm/');
  const canUseCrmSearch = isCrmRoute && hasPermission('crm:search:read:self');
  const canSwitchBusinessLine = isCrmRoute && hasPermission('crm:product:read:tenant');
  // P3-3/P3-3b: the operating-unit switcher is for ANY CRM user in >1 unit (reps
  // included) — it reads the caller's own reachable units and self-hides at ≤1.
  const canSwitchUnit = isCrmRoute && hasPermission('crm:access:read:tenant');
  // R30-021: useLogout() mutation calls POST /auth/logout server-side so the
  // token gets added to Redis denylist + JWT strategy rejects future use.
  // Previously this used the bare store action `useAuthActions().logout()`
  // which only cleared localStorage — no API call, no server-side
  // invalidation, token usable for its full TTL. The mutation hook
  // internally handles store clear + toast + navigate('/login') in both
  // onSuccess and onError paths.
  const logoutMutation = useLogout();

  const handleLogout = () => {
    logoutMutation.mutate();
  };

  const toggleTheme = () => {
    toggleDarkMode();
  };

  if (!user) return null;

  const displayName =
    user.fullName?.trim() ||
    [user.firstName, user.lastName].filter(Boolean).join(' ').trim() ||
    user.email ||
    'User';
  const userInitials =
    `${user.firstName?.trim()?.[0] ?? ''}${user.lastName?.trim()?.[0] ?? ''}`.toUpperCase() ||
    user.email?.trim()?.[0]?.toUpperCase() ||
    'U';
  const roleLabel = getRoleDisplayName(user.role);

  return (
    <header
      className={cn(
        'fixed top-0 right-0 z-30 h-16 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 transition-all duration-300',
        'left-0', // Default for mobile
        sidebarCollapsed ? 'lg:left-16' : 'lg:left-64', // Desktop adjusts for sidebar
      )}
    >
      <div className="flex h-full items-center gap-4 px-4 lg:px-6">
        {/* Mobile Menu Button */}
        <Button
          variant="ghost"
          size="icon"
          className="lg:hidden"
          onClick={onMenuClick}
          aria-label="Open navigation"
          title="Open navigation"
        >
          <Menu className="h-5 w-5" />
        </Button>

        {canUseCrmSearch && (
          <div className="hidden md:flex flex-1 max-w-md">
            <GlobalSearchBar />
          </div>
        )}

        {/* Business-line switcher (inventory layer, v1.4 FR-37) — the
            component self-hides when the flag is off or the tenant has a
            single line; the permission gate mirrors the backend read guard. */}
        {canSwitchBusinessLine && <BusinessLineSwitcher />}

        {/* Workspace breadcrumb (company › line › unit) — always visible so the
            user knows which legal entity they're operating as, and doubles as
            the unit switcher / jump-to-overview for multi-unit and admin users. */}
        {canSwitchUnit && <WorkspaceBreadcrumb />}

        {/* Right Section */}
        <div className="ml-auto flex items-center gap-2">
          {/* Theme Toggle */}
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleTheme}
            className="hidden sm:flex"
            aria-label={isDark ? 'Use light theme' : 'Use dark theme'}
            title={isDark ? 'Use light theme' : 'Use dark theme'}
          >
            {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </Button>

          {/* Notifications */}
          <NotificationCenter onSettingsClick={() => navigate({ to: '/settings/notifications' })} />

          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="gap-2 px-2"
                aria-label={`Open account menu for ${displayName}`}
                title="Account menu"
              >
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="bg-gradient-to-br from-blue-600 to-blue-400 text-white text-sm">
                    {userInitials}
                  </AvatarFallback>
                </Avatar>
                <div className="hidden md:block text-left">
                  <p className="text-sm font-medium leading-none">{displayName}</p>
                  <p className="text-xs text-muted-foreground">{roleLabel}</p>
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="flex flex-col space-y-1">
                  <p className="text-sm font-medium">{displayName}</p>
                  <p className="text-xs text-muted-foreground">{user.email}</p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate({ to: '/settings/profile' })}>
                <User className="mr-2 h-4 w-4" />
                <span>Profile</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate({ to: '/settings' })}>
                <Settings className="mr-2 h-4 w-4" />
                <span>Settings</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-destructive">
                <LogOut className="mr-2 h-4 w-4" />
                <span>Log out</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
